import qs from 'qs';
import type { AxiosError, AxiosResponse } from 'axios';

import api, { PaginatedResponse } from './config';

import Project from '@global-interfaces/Project';
import Invoice, { InboxInvoice, QBPayrollTimeEntry } from '@global-interfaces/Invoice';
import { HistoryCollection } from '@global-interfaces/History';

export interface InvoiceDocumentMetadata {
  id: number;
  has_file: boolean;
  file_name: string | null;
}

export interface BudgetReport {
  document_url: string;
  project: Project;
  application_no: string;
  file_extension: string;
  date_created: string;
  origin: 'budget_export_excel' | 'draw_pay_app';
}

interface BudgetReportResponse {
  message?: string;
  result?: BudgetReport[];
}

export interface ErpSyncResponse {
  Message: string;
  success: boolean;
  invoice_id?: number;
  line_items_missing?: boolean;
  user?: number;
}

export interface Sage100Reason {
  code: string;
  message: string;
}

export interface Sage100ReadyInvoice {
  id: number;
  invoice_id: string;
  vendor: string;
  amount_total: string;
  warnings: string[];
}

export interface Sage100BlockedInvoice {
  id: number;
  invoice_id: string;
  reasons: Sage100Reason[];
}

export interface Sage100CheckResponse {
  ready: Sage100ReadyInvoice[];
  blocked: Sage100BlockedInvoice[];
  already_exported: number[];
}

export interface Sage100SkippedInvoice {
  id: number;
  reasons: Sage100Reason[];
}

// Vendor payments export (EI Dynamics "AP - 4-3 Vendor Payments"): one entry per payment of an invoice.
export interface Sage100ReadyPayment {
  id: number;
  payment_id: number;
  invoice_id: string;
  vendor: string;
  amount: string;
  payment_date: string;
  payment_type: 'C' | 'OB' | 'CC';
  reference: string;
  warnings: string[];
}

export interface Sage100BlockedPayment {
  id: number;
  payment_id: number | null;
  invoice_id: string;
  reasons: Sage100Reason[];
}

export interface Sage100PaymentsCheckResponse {
  ready: Sage100ReadyPayment[];
  blocked: Sage100BlockedPayment[];
  already_exported: number[];
  // ISO date: payments dated before it are not exported (they are most likely already in Sage). Null: no cutoff.
  payments_start_date: string | null;
}

export interface Sage100SkippedPayment extends Sage100SkippedInvoice {
  payment_id?: number | null;
}

export type Sage100ExportResult =
  | { status: 'downloaded'; blob: Blob; filename: string; skipped: Sage100SkippedPayment[] | null }
  | { status: 'queued'; message: string }
  | { status: 'in_progress'; message: string }
  | { status: 'confirmation_required'; alreadyExported: number[] };

export class Sage100ExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Sage100ExportError';
  }
}

export const SAGE100_DEFAULT_FILENAME = 'sage100-apinv.csv';
export const SAGE100_PAYMENTS_DEFAULT_FILENAME = 'sage100-payments.csv';
const SAGE100_ENDPOINT = 'sage100/apinv-export/';
const SAGE100_PAYMENTS_ENDPOINT = 'sage100/payments-export/';

const readBlobAsText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

const parseBlobJson = async (data: unknown): Promise<Record<string, unknown>> => {
  try {
    const text = data instanceof Blob ? await readBlobAsText(data) : typeof data === 'string' ? data : '';
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const parseSage100Filename = (
  contentDisposition?: string | null,
  defaultFilename = SAGE100_DEFAULT_FILENAME
): string => {
  if (!contentDisposition) {
    return defaultFilename;
  }
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
  return plain?.[1]?.trim() || defaultFilename;
};

// Returns null when the header is missing (e.g. hidden by CORS) or is not valid JSON.
export const parseSage100Skipped = (header?: string | null): Sage100SkippedPayment[] | null => {
  if (!header) {
    return null;
  }
  try {
    const parsed = JSON.parse(header);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export interface ErpAuthResponse {
  Message: string;
  success: boolean;
  url?: string;
  user?: number;
}

const sage100Export = async (
  endpoint: string,
  defaultFilename: string,
  ids: number[],
  confirmReexport: boolean
): Promise<Sage100ExportResult> => {
  let response: AxiosResponse<Blob>;
  try {
    response = await api.post<Blob>(
      endpoint,
      { invoice_ids: ids, check_only: false, confirm_reexport: confirmReexport },
      { responseType: 'blob', validateStatus: (status) => [200, 202, 409].includes(status) }
    );
  } catch (error) {
    // Error bodies arrive as a Blob because of responseType 'blob'; surface the API message.
    const data = (error as AxiosError).response?.data;
    const body = await parseBlobJson(data);
    throw new Sage100ExportError(String(body.message ?? 'The export failed. Please try again or contact support.'));
  }

  const isJson = String(response.headers?.['content-type'] ?? '').includes('application/json');
  if (response.status === 200 && isJson) {
    // The same export is already queued or running: nothing to download.
    const body = await parseBlobJson(response.data);
    return {
      status: 'in_progress',
      message: String(body.message ?? "This export is already being prepared. You'll get it by email when it's ready.")
    };
  }

  if (response.status === 200) {
    return {
      status: 'downloaded',
      blob: response.data,
      filename: parseSage100Filename(response.headers?.['content-disposition'], defaultFilename),
      skipped: parseSage100Skipped(response.headers?.['x-sage100-skipped'])
    };
  }

  const body = await parseBlobJson(response.data);
  if (response.status === 202) {
    return {
      status: 'queued',
      message: String(body.message ?? "The export is being prepared. You'll get it by email and in your past reports.")
    };
  }
  return {
    status: 'confirmation_required',
    alreadyExported: Array.isArray(body.already_exported) ? (body.already_exported as number[]) : []
  };
};

const invoiceAPI = {
  search: async (
    page: number,
    filters: {
      amount_total?: string | number;
      q?: string;
    }
  ) => {
    return api.get('search/invoices/', {
      params: {
        ...filters,
        page: page ? page : 1
      },
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    });
  },
  refreshFile: async (invoiceId: number) => api.post<void>(`/invoices/${invoiceId}/create-invoice-file/`),

  prepareDocumentOpen: async (invoiceId: number, options: { signal?: AbortSignal } = {}) =>
    api.post<InvoiceDocumentMetadata>(
      `/invoices/${invoiceId}/create-invoice-file/`,
      { intent: 'document_open' },
      { signal: options.signal, timeout: 30000 }
    ),
  checkDropboxRules: async (invoiceIdList: number[]) =>
    api.post('/invoices/check-dropbox-rules/', { invoice_id_list: invoiceIdList }),
  v2: {
    list: async (
      page: number,
      params: {
        q?: string | null;
        page?: number;
        page_size?: number;
        vendor_id?: number | number[];
        purchase_order_id?: number;
        project_id?: number | number[];
        billing_period_id?: number | number[];
        payment_method_id?: number | number[];
        docusign_status?: string | string[];
        erp_status?: string | string[];
        flow_status?: string | string[];
        exclude_flow_status?: string | string[];
        procore_status?: string | string[];
        quickbooks_desktop_status?: string | string[];
        quickbooks_status?: string | string[];
        document_origin?: string[];
        active_or_null_projects?: boolean;
      }
    ) =>
      // can be Invoice[] or PaginatedResponse<Invoice>, depending on page_size. If page_size is zero, the backend will return all invoices without pagination
      api.get<PaginatedResponse<Invoice> | Invoice[]>('v2/invoices/', {
        params: { ...params, page: page ? page : 1 },
        paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
      })
  },
  v3: {
    inbox: async (
      page: number,
      params: {
        page_size: number;
        q: string;
        project_id: number[] | undefined;
        vendor_id: number[] | undefined;
        sort: string | null | undefined;
        refresh?: number;
      }
    ) =>
      api.get<PaginatedResponse<InboxInvoice>>('v3/inbox/', {
        params: { ...params, page: page ? page : 1, _t: params.refresh },
        paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
      })
  },
  getLatest: async (params?: {
    project_id?: number | number[];
    vendor_id?: number | number[];
    status?: string | string[];
  }) =>
    api
      .get('inbox/ids/', {
        params: { ...params, limit: 1000 },
        paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
      })
      .then((res) => res.data),
  getInvoice: async (id: number) => api.get<Invoice>(`temp_invoices/${id}/`),
  inboxCount: async (params: { user: number; project_id?: number | number[]; vendor_id?: number | number[] }) =>
    api
      .get('/inbox/counts/', {
        params,
        paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
      })
      .then((res) => res.data),
  inbox: async (
    page: number,
    params: {
      page_size: number;
      q: string;
      project_id: number[] | undefined;
      vendor_id: number[] | undefined;
      sort: string | undefined;
    }
  ) =>
    api.get('inbox/', {
      params: { ...params, page: page ? page : 1 },
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    }),
  latest: async () => api.get(`inbox/latest/`),
  update: async (id: number, data: Partial<Invoice>) => api.patch<Invoice>(`invoices/${id}/`, data),
  bulkUpdate: async (data: {
    ids: number[];
    data: {
      billing_period_id?: number | null | undefined;
      flow_status?: number | null;
      erp_status?: string;
      quickbooks_status?: string;
      quickbooks_desktop_status?: string;
      procore_status?: string;
    };
    function?: string;
  }) => api.patch<Invoice[]>('invoices/bulk-update/', data),
  bulkUpdateList: async (data: { id: number; order_po: number }[]) =>
    api.patch<Invoice[]>('invoices/bulk-update-list/', data),
  delete: async (id: number) => api.delete(`invoices/${id}/`),
  bulkDelete: async (ids: number[]) => api.delete('invoices/bulk-delete/', { data: { ids } }),
  sageSync: async (id: number) => api.post<ErpSyncResponse>(`invoices/${id}/sage-api-sync/`),
  quickBooksSync: async (id: number) => api.post(`invoices/${id}/quickbooks-api-sync/`),
  quickBooksUpdate: async (id: number) => api.post(`/quickbooks/update-invoice/`, { invoice_id: id }),
  quickBooksSyncPayments: async (invoiceIdList: number[]) =>
    api.post('/quickbooks/sync-payments/', { invoice_id_list: invoiceIdList }),
  docuSignSync: async (id: number) => api.post(`invoices/${id}/docusign-signature-by-email/`),
  authQuickBooks: async (id?: number) =>
    api.get<ErpAuthResponse>('oauth/quickbooks/', {
      params: {
        project_id: id
      },
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    }),
  authDocuSign: async () => api.get('oauth/docusign/'),
  oAuthQuickBooks: async (url: string) => api.get(url),
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  getQuickbooksTime: async (params: any) =>
    api.get('quickbooks-time/search-invoices/', {
      params,
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    }),
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  getQuickBooksDesktopTime: async (params: any) =>
    api.get('quickbooks-desktop/get-time-tracking/', {
      params,
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    }),
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  getQuickBooksDesktopLastPayroll: async (params: any) =>
    api.get('quickbooks-desktop/last-payroll/', {
      params,
      paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) }
    }),
  updateQuickBooksDesktopProjectList: async (companyId: number) =>
    api.post('quickbooks-desktop/update-project-list/', { company_id: companyId }),
  updateQuickBooksDesktopVendorList: async (companyId: number) =>
    api.post('quickbooks-desktop/update-vendor-list/', { company_id: companyId }),
  updateQuickBooksDesktopCostCodeList: async (companyId: number) =>
    api.post('quickbooks-desktop/update-cost-code-list/', { company_id: companyId }),
  editQuickBooksDesktopLastPayroll: async (params: {
    results: QBPayrollTimeEntry[];
  }) => api.post('quickbooks-desktop/update-last-payroll/', params),
  quickBooksDesktopSync: async (id: number, ignore_duplicates: boolean) =>
    api.post<ErpSyncResponse>(
      `invoices/${id}/quickbooks-desktop-api-sync/${ignore_duplicates ? '' : '?check_duplicates=true'}`
    ),
  quickBooksDesktopMacSync: async (data = {}) =>
    api.post(`quickbooks-desktop/gen-iif-files/`, { invoice_id_list: data }).then((response) => {
      const type = `${response.headers['content-type']};charset=UTF-8`;
      const blob = new Blob([response.data], { type: type });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'builderpad_qb_desktop_mac_iif_file.iif';
      link.click();
      link.remove();
    }),
  quickBooksDesktopTimeSync: async () => api.post(`quickbooks-desktop/import-time-entries/`),
  quickBooksTimeSync: async (id: number) => api.post(`invoices/${id}/quickbooks-time-api-sync/`),
  authQuickBooksTime: async () => api.get('oauth/quickbooks-time/'),
  checkQuickbooksTime: async () => api.get('oauth/quickbooks/is-connected/'),
  checkConnectedDropBox: async () => api.get('oauth/dropbox/is-connected/'),
  checkConnectedGmail: async () =>
    api.get<{ message: string; success: boolean; email?: string }>('oauth/gmail/is-connected/'),
  checkConnectedOutlook: async () =>
    api.get<{ message: string; success: boolean; email?: string }>('oauth/outlook/is-connected/'),
  checkConnectedDocusign: async () => api.post('oauth/docusign/is-connected/'),
  authQuickBooksDesktop: async () =>
    api.get('quickbooks-desktop/auth/').then((response) => {
      const type = `${response.headers['content-type']};charset=UTF-8`;
      const blob = new Blob([response.data], { type: type });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'builderpad_qb_desktop.qwc';
      link.click();
      link.remove();
    }),
  dropboxConnect: async () => api.get('oauth/dropbox/'),
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  dropboxUpdateRules: async (data: any) => api.post('dropbox/rules/', data),
  dropboxGetRules: async () => api.get('dropbox/rules/'),


  exportToPdf: async (data = {}) => api.post('accounting/budget-draw-pdf/', data),
  exportParentProjectToPdf: async (data = {}) => api.post('accounting/budget-draw-pdf-project-parent/', data),
  getPastBudgetDraws: async () => api.post<BudgetReportResponse>('accounting/past-budget-draws/'),
  historyLog: async (invoiceId: number) => api.get<HistoryCollection>(`history/invoices/${invoiceId}/`),
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  exportPdf: async (data: any) => api.post(`invoice/stamp/`, data),
  sageIntacctExport: async (ids: number[]) =>
    api.post<Blob>(
      `sage-intacct/export/`,
      {
        invoice_ids: ids,
        task: true
      },
      {
        responseType: 'blob'
      }
    ),
  sage100ApinvCheck: async (ids: number[]) =>
    api
      .post<Sage100CheckResponse>(SAGE100_ENDPOINT, {
        invoice_ids: ids,
        check_only: true,
        confirm_reexport: false
      })
      .then((response) => response.data),
  sage100ApinvExport: async (ids: number[], confirmReexport = false): Promise<Sage100ExportResult> =>
    sage100Export(SAGE100_ENDPOINT, SAGE100_DEFAULT_FILENAME, ids, confirmReexport),
  sage100PaymentsCheck: async (ids: number[]) =>
    api
      .post<Sage100PaymentsCheckResponse>(SAGE100_PAYMENTS_ENDPOINT, {
        invoice_ids: ids,
        check_only: true,
        confirm_reexport: false
      })
      .then((response) => response.data),
  sage100PaymentsExport: async (ids: number[], confirmReexport = false): Promise<Sage100ExportResult> =>
    sage100Export(SAGE100_PAYMENTS_ENDPOINT, SAGE100_PAYMENTS_DEFAULT_FILENAME, ids, confirmReexport),
  updateQbdPayroll: async (id: string, payload: Partial<QBPayrollTimeEntry>) =>
    api.patch(`/qbd-payrolls/${id}/`, payload),
  importQuickbooksTimeInvoices: async (ids: number[]) =>
    api.post('quickbooks-time/import-invoices/', { time_tracking_ids: ids })
};

export default invoiceAPI;
